import type { NextApiRequest, NextApiResponse } from 'next';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import OpenAI from 'openai';

const openai = new OpenAI({ apiKey: process.env.OPENAI_API_KEY });

/**
 * POST /api/ai-insights/refresh
 * 
 * Generates AI insights for all dashboards and stores them in Supabase.
 * Meant to be called 2x/day via cron:
 *   curl -X POST https://externaliza2.gabsdev.com/api/ai-insights/refresh \
 *     -H "x-cron-secret: $CRON_SECRET"
 * 
 * Auth: CRON_SECRET header OR admin session
 */
export default async function handler(req: NextApiRequest, res: NextApiResponse) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  // Auth: check cron secret or admin bearer token
  const cronSecret = req.headers['x-cron-secret'];
  const expectedSecret = process.env.CRON_SECRET;

  if (!cronSecret || cronSecret !== expectedSecret) {
    // Fallback: check if caller is admin via bearer token
    const authHeader = req.headers.authorization;
    if (!authHeader?.startsWith('Bearer ')) {
      return res.status(401).json({ error: 'No autorizado' });
    }
    const token = authHeader.split(' ')[1];
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);
    if (error || !user) {
      return res.status(401).json({ error: 'Token inválido' });
    }
    const { data: profile } = await supabaseAdmin
      .from('profiles')
      .select('rol')
      .eq('id', user.id)
      .single();
    if (profile?.rol !== 'admin') {
      return res.status(403).json({ error: 'Solo administradores' });
    }
  }

  try {
    const results = {
      global: 0,
      clientes: 0,
      errors: [] as string[],
    };

    // ── 1) Gather global data ─────────────────────────────────
    const [
      { data: allFinancial },
      { data: allDocs },
      { data: allIncidencias },
      { data: allClientes },
    ] = await Promise.all([
      supabaseAdmin.from('datos_financieros').select('*'),
      supabaseAdmin.from('documentos').select('id, estado, cliente_id'),
      supabaseAdmin.from('incidencias').select('id, estado, cliente_id'),
      supabaseAdmin.from('clientes').select('id, razon_social, nombre_comercial, nif'),
    ]);

    // ── 2) Generate GLOBAL admin insights ─────────────────────
    const globalContext = buildGlobalContext(allFinancial || [], allDocs || [], allIncidencias || [], allClientes || []);

    if (globalContext) {
      const globalInsights = await generateInsightsWithAI('global', null, globalContext);
      if (globalInsights.length > 0) {
        // Delete old global insights
        await supabaseAdmin
          .from('ai_dashboard_insights')
          .delete()
          .eq('scope', 'global');

        const { error: insertErr } = await supabaseAdmin
          .from('ai_dashboard_insights')
          .insert(globalInsights);

        if (insertErr) {
          results.errors.push(`Global insert: ${insertErr.message}`);
        } else {
          results.global = globalInsights.length;
        }
      }
    }

    // ── 3) Generate PER-CLIENT insights ───────────────────────
    for (const cliente of (allClientes || [])) {
      try {
        const clienteDocs = (allDocs || []).filter(d => d.cliente_id === cliente.id);
        const clienteInc = (allIncidencias || []).filter(i => i.cliente_id === cliente.id);
        const clienteFin = (allFinancial || []).filter(f => (f as any).cliente_id === cliente.id);

        const clienteContext = buildClienteContext(
          cliente,
          clienteFin,
          clienteDocs,
          clienteInc
        );

        if (!clienteContext) continue;

        const clienteInsights = await generateInsightsWithAI('cliente', cliente.id, clienteContext);
        if (clienteInsights.length > 0) {
          // Delete old insights for this client
          await supabaseAdmin
            .from('ai_dashboard_insights')
            .delete()
            .eq('scope', 'cliente')
            .eq('target_id', cliente.id);

          const { error: insertErr } = await supabaseAdmin
            .from('ai_dashboard_insights')
            .insert(clienteInsights);

          if (insertErr) {
            results.errors.push(`Cliente ${cliente.id}: ${insertErr.message}`);
          } else {
            results.clientes++;
          }
        }
      } catch (err: any) {
        results.errors.push(`Cliente ${cliente.id}: ${err.message}`);
      }
    }

    return res.status(200).json({
      success: true,
      generados: {
        global: results.global,
        clientes: results.clientes,
      },
      errores: results.errors.length > 0 ? results.errors : undefined,
      timestamp: new Date().toISOString(),
    });
  } catch (err: any) {
    console.error('AI insights refresh error:', err);
    return res.status(500).json({ error: err.message || 'Error interno' });
  }
}

// ── Context builders ──────────────────────────────────────────

function buildGlobalContext(
  financial: any[],
  docs: any[],
  incidencias: any[],
  clientes: any[]
): string | null {
  if (clientes.length === 0) return null;

  const totalIngresos = financial.reduce((s, f) => s + (parseFloat(f.ingresos) || 0), 0);
  const totalGastos = financial.reduce((s, f) => s + (parseFloat(f.gastos) || 0), 0);
  const resultado = totalIngresos - totalGastos;
  const margen = totalIngresos > 0 ? ((resultado / totalIngresos) * 100) : 0;

  const docsPendientes = docs.filter(d => d.estado === 'pendiente' || d.estado === 'procesado_ia').length;
  const docsContabilizados = docs.filter(d => d.estado === 'contabilizado').length;
  const incAbiertas = incidencias.filter(i => i.estado === 'nueva' || i.estado === 'en_revision').length;

  // Expense distribution by client
  const gastosPorCliente = new Map<string, number>();
  for (const f of financial) {
    const cid = f.cliente_id;
    gastosPorCliente.set(cid, (gastosPorCliente.get(cid) || 0) + (parseFloat(f.gastos) || 0));
  }

  return `
DATOS GLOBALES DE LA ASESORÍA:
- ${clientes.length} clientes activos
- Ingresos totales: ${totalIngresos.toFixed(2)}€
- Gastos totales: ${totalGastos.toFixed(2)}€
- Resultado: ${resultado.toFixed(2)}€
- Margen: ${margen.toFixed(1)}%
- Documentos pendientes: ${docsPendientes}
- Documentos contabilizados: ${docsContabilizados}
- Incidencias abiertas: ${incAbiertas}
- Clientes con gastos: ${gastosPorCliente.size}
`.trim();
}

function buildClienteContext(
  cliente: any,
  financial: any[],
  docs: any[],
  incidencias: any[]
): string | null {
  if (docs.length === 0 && financial.length === 0) return null;

  const nombre = cliente.nombre_comercial || cliente.razon_social || 'Sin nombre';
  const totalIngresos = financial.reduce((s, f) => s + (parseFloat(f.ingresos) || 0), 0);
  const totalGastos = financial.reduce((s, f) => s + (parseFloat(f.gastos) || 0), 0);
  const resultado = totalIngresos - totalGastos;

  const docsPendientes = docs.filter(d => d.estado === 'pendiente' || d.estado === 'procesado_ia').length;
  const docsContabilizados = docs.filter(d => d.estado === 'contabilizado').length;
  const incAbiertas = incidencias.filter(i => i.estado === 'nueva' || i.estado === 'en_revision').length;

  // Monthly evolution
  const meses = new Map<string, { ingresos: number; gastos: number }>();
  for (const f of financial) {
    const periodo = f.periodo as string;
    if (!meses.has(periodo)) meses.set(periodo, { ingresos: 0, gastos: 0 });
    const m = meses.get(periodo)!;
    m.ingresos += parseFloat(f.ingresos) || 0;
    m.gastos += parseFloat(f.gastos) || 0;
  }

  const evolucion = [...meses.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([p, d]) => `  ${p}: Ingresos ${d.ingresos.toFixed(0)}€, Gastos ${d.gastos.toFixed(0)}€`)
    .join('\n');

  return `
CLIENTE: ${nombre} (NIF: ${cliente.nif || 'N/A'})
- Ingresos totales: ${totalIngresos.toFixed(2)}€
- Gastos totales: ${totalGastos.toFixed(2)}€
- Resultado: ${resultado.toFixed(2)}€
- Documentos pendientes: ${docsPendientes}
- Documentos contabilizados: ${docsContabilizados}
- Incidencias abiertas: ${incAbiertas}
${evolucion ? `\nEVOLUCIÓN MENSUAL:\n${evolucion}` : ''}
`.trim();
}

// ── AI generation ─────────────────────────────────────────────

interface InsightRow {
  scope: string;
  target_id: string | null;
  tipo: string;
  titulo: string;
  contenido: string;
  metadata: Record<string, any>;
  prioridad: string;
  activo: boolean;
}

async function generateInsightsWithAI(
  scope: 'global' | 'cliente',
  targetId: string | null,
  context: string
): Promise<InsightRow[]> {
  const systemPrompt = scope === 'global'
    ? `Eres un analista financiero senior de una asesoría contable española. Genera insights concisos y accionables basados en los datos globales de la asesoría. Responde SIEMPRE en español.`
    : `Eres un analista financiero senior. Genera insights concisos y accionables para el cliente específico. Responde SIEMPRE en español.`;

  const userPrompt = scope === 'global'
    ? `Basándote en estos datos de la asesoría, genera un JSON con la siguiente estructura exacta:
{
  "resumen": "Resumen ejecutivo en 2-3 frases",
  "comentario_financiero": "Análisis financiero detallado en 3-5 frases con recomendaciones concretas",
  "alertas": [{"titulo": "...", "contenido": "...", "prioridad": "alta|media|baja"}],
  "recomendaciones": [{"titulo": "...", "contenido": "..."}],
  "distribucion_gastos": [{"categoria": "...", "porcentaje": N}]
}

REGLAS:
- Máximo 3 alertas, 3 recomendaciones, 5 categorías de gastos
- Las alertas deben ser problemas reales detectados en los datos
- Las recomendaciones deben ser accionables
- La distribución de gastos debe ser una estimación razonable basada en el sector asesoría contable española
- Si los datos son insuficientes, genera igualmente con lo disponible
- NO inventes datos numéricos que no estén en el contexto

DATOS:
${context}`
    : `Basándote en estos datos del cliente, genera un JSON con la siguiente estructura exacta:
{
  "resumen": "Resumen del estado del cliente en 2-3 frases",
  "alertas": [{"titulo": "...", "contenido": "...", "prioridad": "alta|media|baja"}],
  "recomendaciones": [{"titulo": "...", "contenido": "..."}],
  "tendencias": [{"label": "...", "value": "...", "tendencia": "up|down|stable"}]
}

REGLAS:
- Máximo 3 alertas, 3 recomendaciones, 4 tendencias
- Las alertas deben reflejar problemas reales en los datos
- Las tendencias deben basarse en la evolución mensual si está disponible
- Las recomendaciones deben ser específicas y accionables
- NO inventes datos numéricos que no estén en el contexto

DATOS:
${context}`;

  try {
    const completion = await openai.chat.completions.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 1500,
      response_format: { type: 'json_object' },
    });

    const raw = completion.choices[0]?.message?.content;
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const rows: InsightRow[] = [];

    // Resumen
    if (parsed.resumen) {
      rows.push({
        scope,
        target_id: targetId,
        tipo: 'resumen',
        titulo: 'Resumen',
        contenido: parsed.resumen,
        metadata: {},
        prioridad: 'media',
        activo: true,
      });
    }

    // Comentario financiero (global only)
    if (parsed.comentario_financiero) {
      rows.push({
        scope,
        target_id: targetId,
        tipo: 'comentario_financiero',
        titulo: 'Análisis Financiero',
        contenido: parsed.comentario_financiero,
        metadata: {},
        prioridad: 'media',
        activo: true,
      });
    }

    // Alertas
    if (Array.isArray(parsed.alertas)) {
      for (const a of parsed.alertas.slice(0, 3)) {
        rows.push({
          scope,
          target_id: targetId,
          tipo: 'alerta',
          titulo: a.titulo || 'Alerta',
          contenido: a.contenido || a.titulo,
          metadata: {},
          prioridad: a.prioridad || 'media',
          activo: true,
        });
      }
    }

    // Recomendaciones
    if (Array.isArray(parsed.recomendaciones)) {
      for (const r of parsed.recomendaciones.slice(0, 3)) {
        rows.push({
          scope,
          target_id: targetId,
          tipo: 'recomendacion',
          titulo: r.titulo || 'Recomendación',
          contenido: r.contenido || r.titulo,
          metadata: {},
          prioridad: 'media',
          activo: true,
        });
      }
    }

    // Tendencias (client only)
    if (Array.isArray(parsed.tendencias)) {
      for (const t of parsed.tendencias.slice(0, 4)) {
        rows.push({
          scope,
          target_id: targetId,
          tipo: 'tendencia',
          titulo: t.label || 'Tendencia',
          contenido: t.value || '',
          metadata: { tendencia: t.tendencia || 'stable' },
          prioridad: 'media',
          activo: true,
        });
      }
    }

    // Distribución de gastos (global only)
    if (Array.isArray(parsed.distribucion_gastos)) {
      rows.push({
        scope,
        target_id: targetId,
        tipo: 'distribucion_gastos',
        titulo: 'Distribución de Gastos',
        contenido: JSON.stringify(parsed.distribucion_gastos),
        metadata: { categorias: parsed.distribucion_gastos },
        prioridad: 'media',
        activo: true,
      });
    }

    return rows;
  } catch (err: any) {
    console.error(`AI generation error for ${scope}/${targetId}:`, err.message);
    return [];
  }
}
